import { Button, Radio } from "@material-tailwind/react";
import { useState, useContext, useEffect } from "react";
import { YearlyPlan } from "../../mock/PricingData";
import { useNavigate, useLocation } from "react-router-dom";
import { AppContext } from "../../context/AppContext";
import { toast } from "react-toastify";
import axios from "axios";

const fallbackMemberships = {
  "bronze": { id: 101, annuallyCost: 0, monthlyCost: 0 },
  "copper": { id: 102, annuallyCost: 29.99, monthlyCost: 2.99 },
  "silver": { id: 103, annuallyCost: 99.99, monthlyCost: 9.99 },
  "gold": { id: 104, annuallyCost: 36000, monthlyCost: 3600 },
  "platinum": { id: 105, annuallyCost: 54000, monthlyCost: 5400 },
  "titanium": { id: 106, annuallyCost: 72000, monthlyCost: 7200 },
};

const PricingPlan = () => {
  const [membershipType, setMembershipType] = useState("Yearly");
  const navigate = useNavigate();
  const location = useLocation();
  const { profileType } = useContext(AppContext);
  const BASE_URL = `${process.env.REACT_APP_BASE_URL}`;

  const [memberships, setMemberships] = useState([]);
  const [discountInput, setDiscountInput] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(null); // { code, percentage, applicablePlans, billingCycles }
  const [validatingDiscount, setValidatingDiscount] = useState(false);

  useEffect(() => {
    const fetchMemberships = async () => {
      try {
        const response = await axios.get(`${BASE_URL}api/get-all-membership-subscription`);
        if (response.data && response.data.length > 0) {
          setMemberships(response.data);
        }
      } catch (error) {
        console.error("Error fetching memberships:", error);
      }
    };
    fetchMemberships();
  }, [BASE_URL]);

  const handleApplyDiscount = async () => {
    if (!discountInput.trim()) return;
    try {
      setValidatingDiscount(true);
      const response = await axios.get(`${BASE_URL}api/discounts/active`);
      const discountsList = response.data?.commonResponse || response.data?.Result || response.data || [];
      const codeUpper = discountInput.trim().toUpperCase();
      const matched = discountsList.find(d => d.code.toUpperCase() === codeUpper);
      
      if (matched) {
        const applicablePlans = matched.applicable_plans || matched.applicablePlans || [];
        const billingCycles = matched.billing_cycles || matched.billingCycles || [];
        setAppliedDiscount({
          code: matched.code,
          percentage: matched.percentage,
          applicablePlans: applicablePlans.map(p => p.toLowerCase()),
          billingCycles: billingCycles.map(c => c.toLowerCase())
        });
        toast.success(`Discount code ${matched.code} applied successfully! (${matched.percentage}% off)`);
      } else {
        // Fallback: Try validating directly via POST /api/discounts/validate
        try {
          const valResponse = await axios.post(`${BASE_URL}api/discounts/validate`, {
            code: discountInput.trim(),
            plan: "gold",
            billing_cycle: membershipType.toLowerCase()
          });
          const valData = valResponse.data?.commonResponse || valResponse.data;
          if (valData && valData.valid) {
            setAppliedDiscount({
              code: valData.discount_code || discountInput.trim(),
              percentage: valData.percentage,
              applicablePlans: ["copper", "silver", "gold", "platinum", "titanium"],
              billingCycles: [membershipType.toLowerCase()]
            });
            toast.success(`Discount code applied successfully! (${valData.percentage}% off)`);
          } else {
            toast.error(valData?.reason || "Invalid discount code.");
          }
        } catch (valErr) {
          toast.error(valErr.response?.data?.message || "Invalid discount code.");
        }
      }
    } catch (err) {
      console.error("Error applying discount:", err);
      toast.error("Failed to apply discount code.");
    } finally {
      setValidatingDiscount(false);
    }
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountInput("");
    toast.info("Discount code removed.");
  };

  const getDiscountedPrice = (planName, originalPrice) => {
    if (
      appliedDiscount &&
      appliedDiscount.applicablePlans.includes(planName.toLowerCase()) &&
      appliedDiscount.billingCycles.includes(membershipType.toLowerCase())
    ) {
      const discountAmount = originalPrice * (appliedDiscount.percentage / 100);
      return Math.max(0, originalPrice - discountAmount);
    }
    return null;
  };

  return (
    <div>
      <h1 className="text-xl font-semibold text-center">
        Select Your Membership
      </h1>
      
      {/* Selector & Discount Input Box */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mt-6 mb-8 p-6 bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-100 dark:border-gray-700">
        <div className="flex gap-8">
          <Radio
            name="type"
            label="Yearly"
            checked={membershipType === "Yearly"}
            onChange={() => setMembershipType("Yearly")}
            labelProps={{ className: "text-gray-900 dark:text-white" }}
          />
          <Radio
            name="type"
            label="Monthly"
            checked={membershipType === "Monthly"}
            onChange={() => setMembershipType("Monthly")}
            labelProps={{ className: "text-gray-900 dark:text-white" }}
          />
        </div>

        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Discount Code"
            value={discountInput}
            onChange={(e) => setDiscountInput(e.target.value)}
            disabled={appliedDiscount !== null}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white uppercase focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:opacity-60"
          />
          {appliedDiscount ? (
            <div className="flex items-center gap-2">
              <span className="text-xs bg-green-150 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2.5 py-1.5 rounded-lg font-bold border border-green-200">
                Applied: {appliedDiscount.code} ({appliedDiscount.percentage}% Off)
              </span>
              <button
                type="button"
                onClick={handleRemoveDiscount}
                className="text-xs text-red-500 hover:text-red-700 font-semibold px-2"
              >
                Remove
              </button>
            </div>
          ) : (
            <Button
              size="sm"
              color="blue"
              onClick={handleApplyDiscount}
              disabled={validatingDiscount}
            >
              {validatingDiscount ? "Validating..." : "Apply Code"}
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-8 lg:grid lg:grid-cols-3 sm:gap-6 xl:gap-10 py-5 lg:space-y-0">
        {YearlyPlan.map((plan, index) => {
          const originalPrice = membershipType === "Yearly" ? plan.yearprice : plan.monthprice;
          const discountedPrice = getDiscountedPrice(plan.plan, originalPrice);

          return (
            <div
              key={index}
              onClick={async () => {
                const token = localStorage.getItem("accessToken");
                if (token) {
                  if (location.pathname === "/user-membership") {
                    try {
                      toast.info(`Initiating upgrade/downgrade for ${plan.plan} (${membershipType})...`);
                      const userId = localStorage.getItem("userId");
                      const email = localStorage.getItem("email");
                      const planNameLower = plan.plan.toLowerCase();
                      
                      const matchedDbPlan = memberships.find(
                        m => m.MembershipType.toLowerCase() === planNameLower
                      );
                      
                      const mId = matchedDbPlan?.MembershipId || fallbackMemberships[planNameLower]?.id || 104;
                      const mType = matchedDbPlan?.MembershipType || plan.plan;
                      const cost = membershipType === "Yearly"
                        ? (matchedDbPlan?.AnnuallyCost || fallbackMemberships[planNameLower]?.annuallyCost || 36000)
                        : (matchedDbPlan?.MonthlyCost || fallbackMemberships[planNameLower]?.monthlyCost || 3600);

                      const orderTableData = {
                        membership: {
                          membershipId: parseInt(mId),
                          membershipType: mType
                        },
                        currency: "USD",
                        amount: parseFloat(cost),
                        isConfirmed: "true",
                        user: {
                          userId: parseInt(userId)
                        },
                        email: email,
                        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
                        isUpdate: true,
                        discountCode: appliedDiscount?.code || null
                      };

                      const response = await axios.post(`${BASE_URL}stripe/payment`, orderTableData);
                      let responseData = response.data;
                      if (typeof responseData === 'string') {
                        responseData = JSON.parse(responseData);
                      }

                      if (responseData.status === "success" && responseData.url) {
                        window.location.href = responseData.url;
                      } else {
                        toast.error(responseData.message || "Failed to start payment session.");
                      }
                    } catch (err) {
                      console.error("Direct Stripe redirection failed:", err);
                      toast.error(err.response?.data?.message || "Error connecting to Stripe.");
                    }
                  } else {
                    navigate('/checkout', { state: { planName: plan.plan, billingCycle: membershipType, discountCode: appliedDiscount?.code } });
                  }
                } else {
                  if (!profileType) {
                    toast.warn("Please select Profile type first");
                  } else {
                    navigate('/register', { state: { planName: plan.plan, billingCycle: membershipType, discountCode: appliedDiscount?.code } });
                  }
                }
              }}
              className="flex flex-col p-6 mx-auto max-w-lg text-center text-gray-900 bg-white rounded-lg border border-gray-100 shadow dark:border-gray-600 xl:p-8 dark:bg-gray-800 dark:text-white pointer-events-auto cursor-pointer"
            >
              <h3 className="mb-4 text-2xl font-semibold flex items-center gap-2 justify-center">
                <img src={plan.icon} alt={plan.plan} /> {plan.plan}
              </h3>
              
              {/* Dynamic Price Display with discount support */}
              {discountedPrice !== null ? (
                <div className="mb-4">
                  <span className="font-semibold text-red-500 line-through sm:text-base mr-2">
                    ${originalPrice}
                  </span>
                  <span className="font-extrabold text-green-600 dark:text-green-400 sm:text-2xl text-xl">
                    ${discountedPrice.toFixed(2)}
                  </span>
                  <span className="block text-xs font-light text-gray-500 dark:text-gray-400">
                    per {membershipType}
                  </span>
                </div>
              ) : (
                <div className="mb-4">
                  <p className="font-semibold text-gray-800 sm:text-lg dark:text-gray-400">
                    ${originalPrice} per {membershipType}
                  </p>
                </div>
              )}

              <p className="font-light text-gray-500 sm:text-lg dark:text-gray-400">
                Billed {membershipType}
              </p>
              <Button color="blue" className="mt-5">
                Try Now
              </Button>
              <ul className="mb-8 space-y-4 text-left mt-5">
                {membershipType === "Yearly"
                  ? plan.yearfacilities.map((facility, index) => (
                    <li key={index} className="flex items-center space-x-3">
                      <svg
                        className="flex-shrink-0 w-5 h-5 text-green-500 dark:text-green-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        ></path>
                      </svg>
                      <span>{facility}</span>
                    </li>
                  ))
                  : plan.monthfacilities.map((facility, index) => (
                    <li key={index} className="flex items-center space-x-3">
                      <svg
                        className="flex-shrink-0 w-5 h-5 text-green-500 dark:text-green-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        ></path>
                      </svg>
                      <span>{facility}</span>
                    </li>
                  ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PricingPlan;
